/**
 * The official CentraOne wordmark (public/assets/logos).
 *
 * Light mode shows the colour logo; dark mode (the `dark` class applyTheme sets
 * on <html>, for every theme) shows the white one. `tone="white"` forces the
 * white logo for surfaces that are always dark.
 *
 * `mark` crops the wordmark to its circular "1" symbol, for the collapsed rail.
 * The source images are 8000×1403 with the symbol spanning 62.5%–77.5% of the
 * width, so the crop is plain geometry on the rendered height.
 */
const LOGO_RATIO = 8000 / 1403;
const MARK_START = 0.625;
const MARK_WIDTH = 0.15;

const COLOR_SRC = '/assets/logos/logo-color.png';
const WHITE_SRC = '/assets/logos/logo-white.png';

export function BrandLogo({
  height = 26,
  mark = false,
  tone = 'auto',
  className = '',
}: {
  /** Rendered height in px. */
  height?: number;
  /** Show only the circular "1" symbol. */
  mark?: boolean;
  /** 'auto' follows light/dark mode; 'white' always uses the white logo. */
  tone?: 'auto' | 'white';
  className?: string;
}) {
  const fullWidth = height * LOGO_RATIO;

  const img = (src: string, visibility: string) => (
    <img
      src={src}
      alt="CentraOne"
      draggable={false}
      className={`block max-w-none select-none ${visibility}`}
      style={{
        height,
        width: fullWidth,
        marginLeft: mark ? -fullWidth * MARK_START : 0,
      }}
    />
  );

  return (
    <span
      className={`inline-block flex-none overflow-hidden ${className}`}
      style={{ height, width: mark ? fullWidth * MARK_WIDTH : fullWidth }}
    >
      {tone === 'white' ? (
        img(WHITE_SRC, '')
      ) : (
        <>
          {img(COLOR_SRC, 'dark:hidden')}
          {img(WHITE_SRC, 'hidden dark:block')}
        </>
      )}
    </span>
  );
}

export default BrandLogo;
